// Bomb Tag / Hot Potato Client
import { sounds } from '../sound.js';

export class BombTagGameClient {
  constructor(socket, containerEl) {
    this.socket = socket;
    this.container = containerEl;
    this.players = {};
    this.bombHolderId = null;
    this.bombFuse = 15;
    this.obstacles = [];
    this.input = { up: false, down: false, left: false, right: false };

    this.render();
    this.setupSocketEvents();
    this.setupControls();
  }

  render() {
    this.container.innerHTML = `
      <div class="bomb-wrapper">
        <div class="bomb-header">
          <div class="bomb-meta">
            <h2>💣 BOMB TAG ARENA</h2>
            <span class="bomb-sub">WASD/Arrows to Run • Tag friends to pass the bomb • Don't hold it when the fuse blows!</span>
          </div>
          <div id="bt-fuse-badge" class="bomb-badge">FUSE: 15s</div>
        </div>

        <div class="bomb-canvas-box">
          <canvas id="bt-canvas" width="800" height="520"></canvas>
        </div>

        <!-- Virtual Touch D-Pad for Mobile Players -->
        <div class="mobile-touch-bar">
          <div class="touch-dpad-cluster">
            <button id="touch-bt-left" class="btn-virtual-touch">◀</button>
            <button id="touch-bt-right" class="btn-virtual-touch">▶</button>
          </div>
          <div class="touch-action-cluster">
            <button id="touch-bt-up" class="btn-virtual-touch">▲</button>
            <button id="touch-bt-down" class="btn-virtual-touch">▼</button>
          </div>
        </div>
      </div>
    `;

    this.canvas = document.getElementById('bt-canvas');
    this.ctx = this.canvas.getContext('2d');
  }

  setupControls() {
    const handleKey = (e, isDown) => {
      let changed = false;
      const key = e.key ? e.key.toLowerCase() : '';
      if (e.code === 'KeyW' || key === 'w' || e.code === 'ArrowUp') { this.input.up = isDown; changed = true; }
      if (e.code === 'KeyS' || key === 's' || e.code === 'ArrowDown') { this.input.down = isDown; changed = true; }
      if (e.code === 'KeyA' || key === 'a' || e.code === 'ArrowLeft') { this.input.left = isDown; changed = true; }
      if (e.code === 'KeyD' || key === 'd' || e.code === 'ArrowRight') { this.input.right = isDown; changed = true; }

      if (changed) {
        this.socket.emit('bt_input', this.input);
      }
    };

    const attachTouchBtn = (btnId, keyName) => {
      const btn = document.getElementById(btnId);
      if (!btn) return;
      const setPressed = (pressed) => {
        if (this.input[keyName] !== pressed) {
          this.input[keyName] = pressed;
          this.socket.emit('bt_input', this.input);
        }
      };
      btn.addEventListener('touchstart', (e) => { e.preventDefault(); setPressed(true); }, { passive: false });
      btn.addEventListener('touchend', (e) => { e.preventDefault(); setPressed(false); }, { passive: false });
      btn.addEventListener('mousedown', () => setPressed(true));
      btn.addEventListener('mouseup', () => setPressed(false));
      btn.addEventListener('mouseleave', () => setPressed(false));
    };

    attachTouchBtn('touch-bt-left', 'left');
    attachTouchBtn('touch-bt-right', 'right');
    attachTouchBtn('touch-bt-up', 'up');
    attachTouchBtn('touch-bt-down', 'down');

    this.keyDownHandler = (e) => handleKey(e, true);
    this.keyUpHandler = (e) => handleKey(e, false);

    window.addEventListener('keydown', this.keyDownHandler);
    window.addEventListener('keyup', this.keyUpHandler);
  }

  setupSocketEvents() {
    this.socket.on('bt_started', (data) => {
      this.players = data.players;
      this.bombHolderId = data.bombHolderId;
      this.bombFuse = data.bombFuse;
      this.obstacles = data.obstacles;
      sounds.playChime();
      this.draw();
    });

    this.socket.on('bt_state', (data) => {
      this.players = data.players;
      this.bombHolderId = data.bombHolderId;
      this.bombFuse = data.bombFuse;

      const badge = document.getElementById('bt-fuse-badge');
      if (badge) {
        const isMe = this.bombHolderId === this.socket.id;
        badge.innerText = isMe ? `🔥 YOU HAVE THE BOMB! (${this.bombFuse}s)` : `FUSE: ${this.bombFuse}s`;
        badge.style.color = isMe ? '#ef4444' : '#fbbf24';
      }

      this.draw();
    });

    this.socket.on('bt_bombPassed', (data) => {
      sounds.playPop();
    });

    this.socket.on('bt_exploded', (data) => {
      sounds.playExplosion();
    });

    this.socket.on('bt_gameOver', (data) => {
      sounds.playVictory();
      alert(`🏆 LAST SURVIVOR: ${data.winner?.name || 'Player'} survived the Bomb Tag Arena!`);
    });
  }

  draw() {
    if (!this.ctx) return;

    // Dark Arena Background
    this.ctx.fillStyle = '#0a0f1d';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Neon Wall Boundary
    this.ctx.strokeStyle = '#ef4444';
    this.ctx.lineWidth = 4;
    this.ctx.strokeRect(2, 2, this.canvas.width - 4, this.canvas.height - 4);

    // Obstacles
    this.ctx.fillStyle = '#1e293b';
    this.ctx.strokeStyle = '#38bdf8';
    this.ctx.lineWidth = 2;
    this.obstacles.forEach((ob) => {
      this.ctx.fillRect(ob.x, ob.y, ob.w, ob.h);
      this.ctx.strokeRect(ob.x, ob.y, ob.w, ob.h);
    });

    // Draw Players
    for (const id in this.players) {
      const p = this.players[id];
      if (!p.isAlive) continue;

      const hasBomb = id === this.bombHolderId;

      // Player Circle
      this.ctx.fillStyle = p.color;
      this.ctx.shadowColor = p.color;
      this.ctx.shadowBlur = hasBomb ? 20 : 10;
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y, 14, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.shadowBlur = 0;

      // Bomb Graphic on Holder
      if (hasBomb) {
        this.ctx.fillStyle = '#0f172a';
        this.ctx.beginPath();
        this.ctx.arc(p.x, p.y - 18, 9, 0, Math.PI * 2);
        this.ctx.fill();

        // Ticking Fuse
        this.ctx.strokeStyle = '#facc15';
        this.ctx.lineWidth = 2;
        this.ctx.beginPath();
        this.ctx.moveTo(p.x, p.y - 27);
        this.ctx.lineTo(p.x + 4, p.y - 32);
        this.ctx.stroke();

        // Spark
        this.ctx.fillStyle = '#ef4444';
        this.ctx.beginPath();
        this.ctx.arc(p.x + 4, p.y - 32, 3, 0, Math.PI * 2);
        this.ctx.fill();
      }

      // Name Tag
      this.ctx.fillStyle = '#ffffff';
      this.ctx.font = 'bold 11px Rajdhani, sans-serif';
      this.ctx.textAlign = 'center';
      this.ctx.fillText(p.name, p.x, p.y + 24);
    }
  }

  destroy() {
    if (this.keyDownHandler) window.removeEventListener('keydown', this.keyDownHandler);
    if (this.keyUpHandler) window.removeEventListener('keyup', this.keyUpHandler);
    this.socket.off('bt_started');
    this.socket.off('bt_state');
    this.socket.off('bt_bombPassed');
    this.socket.off('bt_exploded');
    this.socket.off('bt_gameOver');
  }
}
